import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import ar from './locales/ar.json';

export type AppLanguage = 'en' | 'ar';

// Languages that render right-to-left.
export const RTL_LANGUAGES: AppLanguage[] = ['ar'];

export const SUPPORTED_LANGUAGES: { code: AppLanguage; labelKey: string }[] = [
  { code: 'en', labelKey: 'language.english' },
  { code: 'ar', labelKey: 'language.arabic' },
];

const STORAGE_KEY = 'projectpulse.language';

function initialLanguage(): AppLanguage {
  const stored =
    typeof localStorage !== 'undefined'
      ? (localStorage.getItem(STORAGE_KEY) as AppLanguage | null)
      : null;
  return stored === 'ar' || stored === 'en' ? stored : 'en';
}

export function isRtl(language: string): boolean {
  return RTL_LANGUAGES.includes(language as AppLanguage);
}

/** Apply the document direction + lang attributes for a language. */
export function applyDocumentDirection(language: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language;
  document.documentElement.dir = isRtl(language) ? 'rtl' : 'ltr';
}

/** Change the active language, persist it, and update the document direction. */
export function setLanguage(language: AppLanguage): void {
  void i18n.changeLanguage(language);
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, language);
  }
  applyDocumentDirection(language);
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    ar: { translation: ar },
  },
  lng: initialLanguage(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

// Keep the document direction in sync on load and on every language change.
applyDocumentDirection(i18n.language);
i18n.on('languageChanged', applyDocumentDirection);

export default i18n;
